// =============================================================================
// components/TodayActionsWidget.js — Bugün Yapılacaklar Kartı (Faz BG)
// =============================================================================
// Dashboard üstünde owner sabah ilk gördüğü kart. Tek tıkla ilgili sayfaya gider.
// Sıralama: error → warning → info → success (boşlar en altta).
// 60sn auto-refresh.
// =============================================================================

import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box, Card, CardContent, Typography, Stack, Chip, Button, CircularProgress,
  IconButton, Tooltip, Divider
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";

const API_BASE = "https://api.miksanplastik.com.tr";

const SEVERITY_COLOR = {
  error:   { bg: '#fee2e2', border: '#dc2626', text: '#991b1b', chip: 'error' },
  warning: { bg: '#fef3c7', border: '#d97706', text: '#92400e', chip: 'warning' },
  info:    { bg: '#dbeafe', border: '#2563eb', text: '#1e40af', chip: 'info' },
  success: { bg: '#d1fae5', border: '#059669', text: '#065f46', chip: 'success' }
};

export default function TodayActionsWidget() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(() => {
    setLoading(true); setError(null);
    const token = localStorage.getItem("token");
    fetch(`${API_BASE}/api/admin/dashboard/today-actions`, {
      headers: { Authorization: `Bearer ${token}` }
    })
      .then(r => r.ok ? r.json() : Promise.reject(new Error('HTTP ' + r.status)))
      .then(setData)
      .catch(e => setError(e.message))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    load();
    // 60sn auto-refresh — sabah panel açık tutulduğunda canlı kalır
    const t = setInterval(load, 60000);
    return () => clearInterval(t);
  }, [load]);

  const activeItems = data ? data.items.filter(i => i.count > 0) : [];
  const allClear = data && activeItems.filter(i => i.severity !== 'success').length === 0;

  return (
    <Card sx={{ mb: 3, border: allClear ? '2px solid #10b981' : '2px solid #1565C0',
                bgcolor: allClear ? '#f0fdf4' : '#f8fafc' }}>
      <CardContent>
        <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1.5}>
          <Box>
            <Typography variant="h6" fontWeight={800} sx={{ color: '#1e293b' }}>
              {allClear ? '✅ Bugün her şey yolunda!' : `📋 Bugün Yapılacaklar`}
              {data && !allClear && (
                <Chip label={data.total_actions} size="small" color="error"
                      sx={{ ml: 1, fontWeight: 700 }} />
              )}
            </Typography>
            {data && (
              <Typography variant="caption" color="text.secondary">
                Bugün {data.summary.orders_today} sipariş · ₺{Number(data.summary.revenue_today).toLocaleString('tr-TR', { minimumFractionDigits: 2 })} ciro
              </Typography>
            )}
          </Box>
          <Tooltip title="Yenile">
            <IconButton size="small" onClick={load} disabled={loading}>
              {loading ? <CircularProgress size={16} /> : <RefreshIcon fontSize="small" />}
            </IconButton>
          </Tooltip>
        </Stack>

        {error && <Typography color="error" variant="caption">Yüklenemedi: {error}</Typography>}

        {data && allClear && (
          <Typography variant="body2" color="text.secondary">
            Onay bekleyen sipariş, BH hatası, düşük stok, iade, yorum yok. Modern bir gün! ☕
          </Typography>
        )}

        {data && !allClear && (
          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 1 }}>
            {activeItems.filter(i => i.severity !== 'success').map(item => {
              const c = SEVERITY_COLOR[item.severity] || SEVERITY_COLOR.info;
              return (
                <Box
                  key={item.key}
                  onClick={() => navigate(item.path)}
                  sx={{
                    p: 1.5, bgcolor: c.bg, borderLeft: `4px solid ${c.border}`,
                    borderRadius: 1, cursor: 'pointer',
                    '&:hover': { transform: 'translateX(2px)', boxShadow: 2 },
                    transition: 'all 0.15s'
                  }}
                >
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <Typography sx={{ fontSize: 22 }}>{item.icon}</Typography>
                    <Box sx={{ flex: 1 }}>
                      <Typography sx={{ fontSize: 11, color: c.text, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                        {item.label}
                      </Typography>
                      <Typography sx={{ fontSize: 22, fontWeight: 800, color: c.text, lineHeight: 1.1 }}>
                        {item.count.toLocaleString('tr-TR')}
                      </Typography>
                    </Box>
                  </Stack>
                </Box>
              );
            })}
          </Box>
        )}

        {data && !allClear && (
          <Box sx={{ mt: 2 }}>
            <Button size="small" variant="text" onClick={() => navigate('/action-center')}>
              Detaylı Action Center →
            </Button>
          </Box>
        )}
      </CardContent>
    </Card>
  );
}
